import type { FleetRow } from "./fleet";

/**
 * What a draft is, said plainly, for the fleet list's detail pane.
 *
 * `ProfileDetail` resolves from the registry and would answer "Unknown profile"
 * for a draft id, which is true of the registry and misleading about the draft:
 * it exists, it is detected on upload, and it can govern a run's reporting.
 * What it cannot do is approve anything, so that is what this panel leads with.
 */
export function DraftProfileDetail({ row }: { row: FleetRow }) {
  return (
    <div className="space-y-3 p-4" data-testid="draft-profile-detail">
      <header className="space-y-1">
        <h3 className="text-lg font-semibold">{row.displayName}</h3>
        <p className="text-xs text-slate-600">
          {row.id} · <span className="font-medium text-amber-700">draft</span>
        </p>
      </header>

      <p className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        Measured in this browser and never committed. It detects its source and governs reporting,
        and it approves nothing: no backfill field, no match-rate floor, and no recovered export.
        Committing its delta to <code>src/profiles/sources/</code> is what turns it into policy.
      </p>

      {row.measuredFrom ? (
        <dl className="space-y-1 text-sm" data-testid="draft-measured-from">
          <div>
            <dt className="inline font-medium">Measured from</dt>{" "}
            <dd className="inline text-slate-700">
              {row.measuredFrom.baselineFileName} against {row.measuredFrom.latestFileName}
            </dd>
          </div>
          <div>
            <dt className="inline font-medium">Measured</dt>{" "}
            <dd className="inline text-slate-700">{formatWhen(row.measuredFrom.measuredAt)}</dd>
          </div>
        </dl>
      ) : null}

      <p className="text-sm text-slate-700" data-testid="draft-last-run">
        {row.lastAnalyzedAt === null
          ? "No run in this browser's analysis cache has been attributed to this draft."
          : `Last analysed ${formatWhen(row.lastAnalyzedAt)} — ${row.lastVerdict}.`}
      </p>
    </div>
  );
}

function formatWhen(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
}
